import { ADMIN_ROLES, Role } from 'data/constants/roles';
import type {
    DocumentTemplateFormValues,
    DocumentTemplateRecord,
    OwnershipScope,
    ServiceFormValues,
    ServiceRecord,
} from 'types/servicesDocuments';

export function isUniversalRecord(serviceProviderId: number | null | undefined): boolean {
    return serviceProviderId == null;
}

export function canEditServicesDocumentRecord(
    role: string | null | undefined,
    serviceProviderId: number | null | undefined,
    currentServiceProviderId: number | null | undefined
): boolean {
    if (role === Role.SUPER_SUPER_ADMIN) {
        return true;
    }
    return (
        Boolean(role) &&
        ADMIN_ROLES.includes(role as Role) &&
        serviceProviderId != null &&
        currentServiceProviderId != null &&
        serviceProviderId === currentServiceProviderId
    );
}

export function localizedServiceName(service: ServiceRecord, language: string | undefined): string {
    if (language === 'fr' && service.name_fr) {
        return service.name_fr;
    }
    if (language === 'es' && service.name_es) {
        return service.name_es;
    }
    return service.name_en;
}

export function localizedTemplateName(
    template: DocumentTemplateRecord,
    language: string | undefined
): string {
    if (language === 'fr' && template.name_fr) {
        return template.name_fr;
    }
    return template.name_en || template.name_fr || '';
}

export function buildServiceWritePayload(values: ServiceFormValues): {
    scope: OwnershipScope;
    service_provider_id?: number;
    service_code: string | null;
    country_code: NonNullable<ServiceFormValues['country_code']>;
    name_en: string;
    name_fr: string | null;
    name_es: string | null;
    is_active: boolean;
    show_in_report: boolean;
} {
    const scope = values.scope;
    const serviceProviderId =
        scope === 'provider' && values.service_provider_id !== ''
            ? Number(values.service_provider_id)
            : undefined;

    return {
        scope,
        ...(serviceProviderId != null ? { service_provider_id: serviceProviderId } : {}),
        service_code: values.service_code.trim() || null,
        country_code: values.country_code as NonNullable<ServiceFormValues['country_code']>,
        name_en: values.name_en.trim(),
        name_fr: values.name_fr.trim() || null,
        name_es: values.name_es.trim() || null,
        is_active: values.is_active,
        show_in_report: values.show_in_report,
    };
}

export function appendDocumentTemplateFormData(
    payload: FormData,
    values: DocumentTemplateFormValues,
    options?: { includeScope?: boolean }
): void {
    payload.append('name_en', values.name_en.trim());
    payload.append('name_fr', values.name_fr.trim());
    payload.append('description_en', values.description_en.trim());
    payload.append('description_fr', values.description_fr.trim());
    payload.append('is_form', String(values.is_form));
    payload.append('document_type', String(values.document_type));

    payload.append('category', values.category.trim());
    payload.append('country_code', values.country_code);
    payload.append('fields_json', values.fields_json.trim());
    if (values.template) {
        payload.append('template', values.template);
    }
    if (values.sample) {
        payload.append('sample', values.sample);
    }

    if (options?.includeScope) {
        payload.append('scope', values.scope);
        if (values.scope === 'provider' && values.service_provider_id !== '') {
            payload.append('service_provider_id', String(values.service_provider_id));
        }
    }
}
